import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import {
  MessageSquare,
  Plus,
  Search,
  Users,
  Archive,
  Pencil,
  Trash2,
  UserPlus,
  Info,
  Loader2,
  Check,
  CheckCheck,
  Pin,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { supabase } from "@/integrations/supabase/client";
import { DEPARTMENT_LABELS, type Department, useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ChatComposer } from "@/components/chat/ChatComposer";
import { ChatMessageBubble, formatMessageTime, type ChatMessageRecord, type SeenInfo } from "@/components/chat/ChatMessageBubble";
import { ImageLightbox } from "@/components/chat/ImageLightbox";
import { ForwardMessageDialog } from "@/components/chat/ForwardMessageDialog";
import {
  ChatRowBody,
  formatListTimestamp,
  getRoomRowInfo,
  previewOfMessage,
} from "@/components/chat/chat-list-row";
import { editMessageText, softDeleteMessage } from "@/lib/chat-message-actions";

export const Route = createFileRoute("/_app/chat")({
  validateSearch: z.object({ roomId: z.string().optional() }),
  component: ChatPage,
});

type RoomType = "group" | "personal";

interface Room {
  id: string;
  name: string | null;
  type: RoomType;
  created_by: string;
  archived: boolean;
  created_at: string;
}
interface Member {
  id: string;
  room_id: string;
  user_id: string;
  role: "admin" | "member";
  joined_at: string;
  last_read_at?: string | null;
  cleared_at?: string | null;
  room_pinned_at?: string | null;
}
interface ChatInvite {
  id: string;
  room_id: string;
  invited_by: string | null;
  invited_user: string;
  status: "pending" | "accepted" | "declined";
  created_at: string;
  room?: { id: string; name: string | null } | null;
  inviter?: { full_name: string; department: Department | null } | null;
}
interface Message extends ChatMessageRecord {
  room_id: string;
}
interface ProfileLite {
  id: string;
  full_name: string;
  avatar_url: string | null;
  department: Department | null;
  status?: string | null;
}
interface Presence {
  user_id: string;
  last_active_at: string;
  is_active: boolean;
}
const ACTIVE_THRESHOLD_MS = 30 * 60 * 1000;
function isActivePresence(p?: Presence) {
  if (!p) return false;
  return p.is_active && Date.now() - new Date(p.last_active_at).getTime() < ACTIVE_THRESHOLD_MS;
}

// Types for chat tables haven't been regenerated yet; use loose typing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb: any = supabase;

function ChatPage() {
  const { user, profile, role } = useAuth();
  const canManageGroups = role === "admin" || role === "moderator";
  const { roomId: roomIdParam } = Route.useSearch();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (roomIdParam) setActiveId(roomIdParam);
  }, [roomIdParam]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showNewPersonal, setShowNewPersonal] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<ChatInvite[]>([]);
  const [replyingTo, setReplyingTo] = useState<{ id: string; text: string; senderName: string } | null>(null);
  const [forwardingMsg, setForwardingMsg] = useState<ChatMessageRecord | null>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [forwardingMulti, setForwardingMulti] = useState<ChatMessageRecord[] | null>(null);
  const [unreadByRoom, setUnreadByRoom] = useState<Record<string, number>>({});
  const [lastMessages, setLastMessages] = useState<Record<string, Message>>({});
  const [presence, setPresence] = useState<Record<string, Presence>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  // Presence load + realtime + heartbeat
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await sb.from("user_presence").select("*");
      if (cancelled) return;
      const map: Record<string, Presence> = {};
      (data ?? []).forEach((p: Presence) => (map[p.user_id] = p));
      setPresence(map);
    })();
    const ch = sb
      .channel("chat-page-presence")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "user_presence" },
        (payload: any) => {
          const p = (payload.new || payload.old) as Presence;
          setPresence((prev) => ({ ...prev, [p.user_id]: payload.new ?? prev[p.user_id] }));
        },
      )
      .subscribe();
    const tick = window.setInterval(() => setPresence((p) => ({ ...p })), 60_000);
    // heartbeat
    let lastSent = 0;
    const ping = async (force = false) => {
      const now = Date.now();
      if (!force && now - lastSent < 60_000) return;
      lastSent = now;
      await sb.from("user_presence").upsert(
        { user_id: user.id, last_active_at: new Date().toISOString(), is_active: true, updated_at: new Date().toISOString() },
        { onConflict: "user_id" },
      );
    };
    ping(true);
    const onActivity = () => { ping(); };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      cancelled = true;
      sb.removeChannel(ch);
      window.clearInterval(tick);
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user?.id]);

  // Load all profiles for picker + display
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("profiles_directory")
        .select("id, full_name, avatar_url, department, status")
        .order("full_name");
      const map: Record<string, ProfileLite> = {};
      (data ?? []).forEach((p) => { if (p.id) map[p.id] = p as ProfileLite; });
      setProfiles(map);
    })();
  }, []);

  // Load rooms (RLS limits to my rooms)
  const loadRooms = useCallback(async () => {
    if (!user) return;
    const { data: ms } = await sb
      .from("chat_members")
      .select("*")
      .eq("user_id", user.id);
    const memberRows = (ms ?? []) as Member[];
    const roomIds = memberRows.map((m) => m.room_id);
    if (roomIds.length === 0) {
      setRooms([]);
      setMembers([]);
      setLastMessages({});
      setUnreadByRoom({});
      return;
    }
    const { data: rs } = await sb
      .from("chat_rooms")
      .select("*")
      .in("id", roomIds)
      .order("created_at", { ascending: false });
    setRooms((rs ?? []) as Room[]);
    const { data: allMembers } = await sb
      .from("chat_members")
      .select("*")
      .in("room_id", roomIds);
    setMembers((allMembers ?? []) as Member[]);

    // Compute unread + last message per room. Fall back to joined_at when never opened.
    const lr: Record<string, string> = {};
    memberRows.forEach((m) => {
      lr[m.room_id] = (m as any).last_read_at ?? m.joined_at;
    });
    const { data: msgs } = await sb
      .from("chat_messages")
      .select("*")
      .in("room_id", roomIds)
      .order("created_at", { ascending: false })
      .limit(500);
    const last: Record<string, Message> = {};
    const unread: Record<string, number> = {};
    (msgs ?? []).forEach((m: Message) => {
      if (!last[m.room_id]) last[m.room_id] = m;
      const lrAt = lr[m.room_id];
      if (
        m.sender_id !== user.id &&
        lrAt &&
        new Date(m.created_at).getTime() > new Date(lrAt).getTime()
      ) {
        unread[m.room_id] = (unread[m.room_id] ?? 0) + 1;
      }
    });
    setLastMessages(last);
    setUnreadByRoom(unread);
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    loadRooms();
    const ch = sb
      .channel("chat-rooms-watch")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_members" },
        () => loadRooms(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_rooms" },
        () => loadRooms(),
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages" },
        () => loadRooms(),
      )
      .subscribe();
    return () => {
      sb.removeChannel(ch);
    };
  }, [user?.id, loadRooms]);

  // Pending invites
  const loadInvites = async () => {
    if (!user) return;
    const { data } = await sb
      .from("chat_invites")
      .select("*, room:chat_rooms(id, name), inviter:profiles!chat_invites_invited_by_fkey(full_name, department)")
      .eq("invited_user", user.id)
      .eq("status", "pending");
    setPendingInvites((data ?? []) as ChatInvite[]);
  };

  useEffect(() => {
    if (!user) return;
    loadInvites();
    const ch = sb
      .channel("chat-invites-watch")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_invites" },
        () => loadInvites(),
      )
      .subscribe();
    return () => { sb.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const acceptInvite = async (invite: ChatInvite) => {
    if (!user) return;
    const { error: mErr } = await sb.from("chat_members").insert({
      room_id: invite.room_id,
      user_id: user.id,
      role: "member",
    });
    if (mErr) { toast.error(mErr.message); return; }
    await sb.from("chat_invites").update({ status: "accepted" }).eq("id", invite.id);
    toast.success("Joined group");
    loadInvites();
    loadRooms();
  };

  const declineInvite = async (invite: ChatInvite) => {
    const { error } = await sb.from("chat_invites").update({ status: "declined" }).eq("id", invite.id);
    if (error) { toast.error(error.message); return; }
    loadInvites();
  };

  const pinChat = async (roomId: string) => {
    if (!user) return;
    const myMemberships = members.filter((m) => m.user_id === user.id);
    const pinnedCount = myMemberships.filter((m) => m.room_pinned_at != null).length;
    if (pinnedCount >= 3) { toast.error("Maximum 3 chats can be pinned."); return; }
    const { error } = await sb
      .from("chat_members")
      .update({ room_pinned_at: new Date().toISOString() })
      .eq("room_id", roomId)
      .eq("user_id", user.id);
    if (error) toast.error(error.message);
    else loadRooms();
  };

  const unpinChat = async (roomId: string) => {
    if (!user) return;
    const { error } = await sb
      .from("chat_members")
      .update({ room_pinned_at: null })
      .eq("room_id", roomId)
      .eq("user_id", user.id);
    if (error) toast.error(error.message);
    else loadRooms();
  };



  // Clear reply state when switching rooms
  useEffect(() => {
    setReplyingTo(null);
    setSelectMode(false);
    setSelectedIds(new Set());
  }, [activeId]);

  // Load messages for active
  useEffect(() => {
    if (!activeId || !user) {
      setMessages([]);
      return;
    }
    const myMember = members.find(
      (m) => m.room_id === activeId && m.user_id === user.id,
    );
    const cleared = myMember?.cleared_at ?? null;
    (async () => {
      let q = sb
        .from("chat_messages")
        .select("*")
        .eq("room_id", activeId)
        .order("created_at", { ascending: true })
        .limit(500);
      if (cleared) q = q.gt("created_at", cleared);
      const { data } = await q;
      setMessages((data ?? []) as Message[]);
    })();
    // Mark room as read
    sb.from("chat_members")
      .update({ last_read_at: new Date().toISOString() })
      .eq("room_id", activeId)
      .eq("user_id", user.id)
      .then(() => {});
    setUnreadByRoom((prev) => {
      if (!prev[activeId]) return prev;
      const next = { ...prev };
      delete next[activeId];
      return next;
    });
    const ch = sb
      .channel(`chat-msgs-${activeId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chat_messages",
          filter: `room_id=eq.${activeId}`,
        },
        (payload: any) => {
          setMessages((prev) => [...prev, payload.new as Message]);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "chat_messages",
          filter: `room_id=eq.${activeId}`,
        },
        (payload: any) => {
          const upd = payload.new as Message;
          setMessages((prev) => prev.map((m) => (m.id === upd.id ? { ...m, ...upd } : m)));
        },
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "chat_messages",
          filter: `room_id=eq.${activeId}`,
        },
        (payload: any) => {
          setMessages((prev) => prev.filter((m) => m.id !== (payload.old as Message).id));
        },
      )
      .subscribe();
    return () => {
      sb.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, user?.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  // Pinned messages
  const [pinnedMessages, setPinnedMessages] = useState<Array<{
    id: string;
    room_id: string;
    message_id: string;
    pinned_by: string | null;
    pinned_at: string;
    message: { id: string; message_text: string | null; attachment_type: string | null; attachment_url: string | null; sender_id: string; created_at: string } | null;
  }>>([]);
  const [pinIndex, setPinIndex] = useState(0);

  const loadPinned = async (roomId: string) => {
    const { data } = await sb
      .from("chat_pinned_messages")
      .select("*, message:chat_messages(id, message_text, attachment_type, attachment_url, sender_id, created_at)")
      .eq("room_id", roomId)
      .order("pinned_at", { ascending: true });
    setPinnedMessages((data ?? []) as any);
  };

  useEffect(() => {
    setPinIndex(0);
    if (activeId) loadPinned(activeId);
    else setPinnedMessages([]);
  }, [activeId]);

  const handlePin = async (messageId: string) => {
    if (!activeId || !user) return;
    if (pinnedMessages.length >= 3) {
      toast.error("Maximum 3 messages can be pinned. Unpin one first.");
      return;
    }
    const { error } = await sb.from("chat_pinned_messages").insert({
      room_id: activeId,
      message_id: messageId,
      pinned_by: user.id,
    });
    if (error) { toast.error(error.message); return; }
    loadPinned(activeId);
  };

  const handleUnpin = async (messageId: string) => {
    if (!activeId) return;
    const { error } = await sb
      .from("chat_pinned_messages")
      .delete()
      .eq("room_id", activeId)
      .eq("message_id", messageId);
    if (error) { toast.error(error.message); return; }
    loadPinned(activeId);
  };

  const scrollToMessage = (messageId: string) => {
    const el = document.getElementById(`msg-${messageId}`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Message action handlers
  const handleReply = (m: ChatMessageRecord) => {
    const sn = profiles[m.sender_id]?.full_name || "Message";
    const text = m.message_text
      ?? (m.attachment_type === "image" ? "📷 Photo" : m.attachment_type === "file" ? "📎 File" : "");
    setReplyingTo({ id: m.id, text, senderName: sn });
  };
  const handleEdit = async (id: string, newText: string) => {
    const err = await editMessageText(id, newText);
    if (err) { toast.error(err); return; }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this message for everyone?")) return;
    const err = await softDeleteMessage(id);
    if (err) toast.error(err);
  };
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };



  const activeRoom = rooms.find((r) => r.id === activeId) || null;
  const activeMembers = members.filter((m) => m.room_id === activeId);

  const visibleRooms = useMemo(() => {
    const q = search.trim().toLowerCase();
    const myMap = new Map(
      members.filter((m) => m.user_id === user!.id).map((m) => [m.room_id, m]),
    );
    return rooms
      .map((r) => ({
        room: r,
        title: roomTitle(r, members, profiles, user!.id),
        isPinned: myMap.get(r.id)?.room_pinned_at != null,
        last: lastMessages[r.id],
        unread: unreadByRoom[r.id] ?? 0,
        activityAt: lastMessages[r.id]?.created_at ?? r.created_at,
      }))
      .filter(({ title }) => !q || title.toLowerCase().includes(q))
      .sort((a, b) => {
        return new Date(b.activityAt).getTime() - new Date(a.activityAt).getTime();
      });
  }, [rooms, members, profiles, search, user, lastMessages, unreadByRoom]);


  // sending handled by <ChatComposer />


  const handleArchiveToggle = async () => {
    if (!activeRoom) return;
    const { error } = await sb
      .from("chat_rooms")
      .update({ archived: !activeRoom.archived })
      .eq("id", activeRoom.id);
    if (error) toast.error(error.message);
    else {
      toast.success(activeRoom.archived ? "Unarchived" : "Archived");
      loadRooms();
    }
  };

  const handleRemoveMember = async (mid: string) => {
    const { error } = await sb.from("chat_members").delete().eq("id", mid);
    if (error) toast.error(error.message);
    else loadRooms();
  };

  const handleDeleteForMe = async (roomId: string) => {
    if (!user) return;
    if (!window.confirm("Delete this chat for you? Other members will still see it.")) return;
    const { error } = await sb
      .from("chat_members")
      .update({ cleared_at: new Date().toISOString() })
      .eq("room_id", roomId)
      .eq("user_id", user.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Chat cleared for you");
    if (activeId === roomId) setActiveId(null);
    loadRooms();
  };

  // People matching current search who aren't already in an existing 1:1
  const peopleMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || !user) return [];
    const personalRooms = rooms.filter((r) => r.type === "personal");
    const existingOtherIds = new Set<string>();
    personalRooms.forEach((r) => {
      const ms = members.filter((m) => m.room_id === r.id);
      const other = ms.find((m) => m.user_id !== user.id);
      if (other && ms.length === 2) existingOtherIds.add(other.user_id);
    });
    return Object.values(profiles)
      .filter((p) => p.id !== user.id && !existingOtherIds.has(p.id))
      .filter((p) => profileMatches(p, q))
      .slice(0, 20);
  }, [search, profiles, rooms, members, user]);

  const startDmWith = async (otherId: string) => {
    if (!user) return;
    const { data: roomId, error } = await sb.rpc("get_or_create_personal_room", {
      _other_user_id: otherId,
    });
    if (error || !roomId) {
      toast.error(error?.message || "Failed to open chat");
      return;
    }
    await loadRooms();
    setActiveId(roomId as string);
    setSearch("");
  };

  return (
    <div className="flex h-[calc(100vh-0px)] min-h-0 flex-1">
      {/* Left: chat list */}
      <aside className="flex w-72 flex-col border-r border-border bg-card">
        <div className="flex items-center justify-between gap-2 border-b border-border p-3">
          <div className="text-sm font-semibold">Chats</div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-[11px]"
              onClick={() => setShowNewPersonal(true)}
            >
              <Plus className="mr-1 h-3 w-3" /> Personal
            </Button>
            {canManageGroups && (
              <Button
                size="sm"
                className="h-7 px-2 text-[11px]"
                onClick={() => setShowNewGroup(true)}
              >
                <Plus className="mr-1 h-3 w-3" /> Group
              </Button>
            )}
          </div>
        </div>
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search chats or people"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>
        </div>
        <ScrollArea className="flex-1">
          {pendingInvites.length > 0 && (
            <div className="px-2 pt-2">
              <p className="px-1 mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
                Invites ({pendingInvites.length})
              </p>
              {pendingInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="mb-1.5 rounded-lg border border-purple-200 bg-purple-50 p-2.5"
                >
                  <p className="text-[12px] font-semibold">{invite.room?.name ?? "Group"}</p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    From {invite.inviter?.full_name ?? "—"}
                    {invite.inviter?.department
                      ? ` · ${DEPARTMENT_LABELS[invite.inviter.department]}`
                      : ""}
                  </p>
                  <div className="mt-2 flex gap-1.5">
                    <button
                      onClick={() => acceptInvite(invite)}
                      className="flex-1 rounded-md bg-green-600 py-1 text-[11px] text-white hover:bg-green-700"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => declineInvite(invite)}
                      className="flex-1 rounded-md border border-border py-1 text-[11px] hover:bg-muted"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {visibleRooms.length === 0 && peopleMatches.length === 0 && pendingInvites.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">
              No chats yet. Start a personal chat or create a group.
            </div>
          ) : (
            <ul className="p-1">
              {visibleRooms.map(({ room, isPinned, last, unread }) => {
                const info = getRoomRowInfo(room, members, profiles, user!.id);
                const online = info.otherUserId ? isActivePresence(presence[info.otherUserId]) : false;
                const preview = previewOfMessage(last);
                return (
                <li key={room.id} className="group/row relative">
                  <button
                    onClick={() => setActiveId(room.id)}
                    className={cn(
                      "flex w-full items-center gap-2.5 rounded-md px-2 py-2 pr-9 text-left transition-colors",
                      activeId === room.id ? "bg-accent" : "hover:bg-accent/50",
                    )}
                  >
                    <ChatRowBody
                      isGroup={info.isGroup}
                      avatarUrl={info.avatarUrl}
                      name={info.name}
                      deptLabel={info.deptLabel}
                      memberCount={info.memberCount}
                      online={online}
                      isPinned={isPinned}
                      preview={preview.text}
                      previewDeleted={preview.deleted}
                      timestamp={last ? formatListTimestamp(last.created_at) : null}
                      unread={unread}
                    />
                  </button>
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Chat options"
                          className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground opacity-0 transition-opacity hover:bg-muted group-hover/row:opacity-100"
                        >
                          <MoreVertical className="h-3 w-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            isPinned ? unpinChat(room.id) : pinChat(room.id);
                          }}
                        >
                          {isPinned ? "📌 Unpin chat" : "📌 Pin chat"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteForMe(room.id);
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          🗑️ Delete for me
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </li>
                );
              })}
              {peopleMatches.length > 0 && (
                <li className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  People
                </li>
              )}
              {peopleMatches.map((p) => (
                <li key={`p-${p.id}`}>
                  <button
                    onClick={() => startDmWith(p.id)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-accent/50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium">{p.full_name}</div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {p.department ? DEPARTMENT_LABELS[p.department] : "Member"} · Start chat
                      </div>
                    </div>
                    <Plus className="h-3 w-3 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </aside>



      {/* Center: active chat */}
      <section className="flex min-w-0 flex-1 flex-col">
        {!activeRoom ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            <div className="flex flex-col items-center gap-2">
              <MessageSquare className="h-8 w-8" />
              Select a chat to start messaging
            </div>
          </div>
        ) : (
          <>
            <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-sm font-semibold">
                    {roomTitle(activeRoom, members, profiles, user!.id)}
                  </h2>
                  {activeRoom.archived && (
                    <Badge variant="secondary" className="text-[10px]">
                      Archived (read-only)
                    </Badge>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {activeRoom.type === "group"
                    ? `Group · ${activeMembers.length} member${activeMembers.length !== 1 ? "s" : ""}`
                    : roomBadgeLabel(activeRoom, members, profiles, user!.id).toUpperCase()}
                </div>
              </div>
              {activeRoom.type === "group" && (
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-[11px]"
                    onClick={() => setShowInvite(true)}
                    title="Invite members"
                  >
                    <UserPlus className="mr-1 h-3 w-3" /> Invite
                  </Button>
                  {canManageGroups && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px]"
                        onClick={() => setShowAddMembers(true)}
                      >
                        <UserPlus className="mr-1 h-3 w-3" /> Add
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px]"
                        onClick={() => setShowRename(true)}
                      >
                        <Pencil className="mr-1 h-3 w-3" /> Rename
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px]"
                        onClick={handleArchiveToggle}
                      >
                        <Archive className="mr-1 h-3 w-3" />
                        {activeRoom.archived ? "Unarchive" : "Archive"}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </header>


            <div className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-[11px] text-amber-800">
              <Info className="mr-1 inline h-3 w-3" />
              Attachments are automatically deleted after 24 hours.
            </div>

            {pinnedMessages.length > 0 && pinnedMessages[pinIndex] && (() => {
              const currentPin = pinnedMessages[pinIndex];
              return (
                <div
                  className="flex cursor-pointer items-center gap-2 border-b border-border bg-muted/30 px-4 py-2 hover:bg-muted/50"
                  onClick={() => scrollToMessage(currentPin.message_id)}
                >
                  <Pin size={12} className="shrink-0 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                      Pinned {pinIndex + 1} of {pinnedMessages.length}
                    </div>
                    <div className="truncate text-[12px]">
                      {currentPin.message?.message_text ?? "📎 Attachment"}
                    </div>
                  </div>
                  {pinnedMessages.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPinIndex((i) => (i + 1) % pinnedMessages.length);
                      }}
                      className="shrink-0 text-[10px] text-primary hover:underline"
                    >
                      Next
                    </button>
                  )}
                </div>
              );
            })()}

            <ScrollArea className="flex-1 bg-background">
              <div ref={scrollRef} className="flex flex-col px-4 py-4">
                {messages.length === 0 && (
                  <div className="py-12 text-center text-xs text-muted-foreground">
                    No messages yet. Say hi 👋
                  </div>
                )}
                {(() => {
                  const msgIndex: Record<string, Message> = {};
                  messages.forEach((mm) => { msgIndex[mm.id] = mm; });
                  return messages.map((m, i) => {
                  const mine = m.sender_id === user?.id;
                  const sender = profiles[m.sender_id];
                  const prev = i > 0 ? messages[i - 1] : null;
                  const showDate =
                    !prev || !isSameDay(prev.created_at, m.created_at);
                  const senderChanged = !prev || prev.sender_id !== m.sender_id;
                  const groupStart = showDate || senderChanged;
                  const seenInfo = computeSeenInfo(
                    m,
                    activeMembers,
                    profiles,
                    user?.id ?? "",
                  );
                  const isPinned = pinnedMessages.some((p) => p.message_id === m.id);
                  const parent = m.reply_to ? msgIndex[m.reply_to] : null;
                  const parentSenderName = parent ? profiles[parent.sender_id]?.full_name ?? null : null;
                  return (
                    <div key={m.id} className={cn("flex flex-col", i === 0 ? "" : groupStart ? "mt-3" : "mt-0.5")}>
                      {showDate && <DateSeparator iso={m.created_at} />}
                      <ChatMessageBubble
                        msg={m}
                        mine={mine}
                        showSender={!mine && senderChanged}
                        senderName={sender?.full_name}
                        onImageClick={setLightboxUrl}
                        variant="page"
                        isPinned={isPinned}
                        onPin={handlePin}
                        onUnpin={handleUnpin}
                        myId={user?.id}
                        isAdmin={canManageGroups}
                        onReply={handleReply}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onCopy={handleCopy}
                        onForward={setForwardingMsg}
                        onScrollToParent={scrollToMessage}
                        selectMode={selectMode}
                        selected={selectedIds.has(m.id)}
                        onEnterSelect={(id) => {
                          setSelectMode(true);
                          setSelectedIds(new Set([id]));
                        }}
                        onToggleSelect={(id) => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(id)) next.delete(id);
                            else next.add(id);
                            return next;
                          });
                        }}
                        parentPreview={parent ? {
                          id: parent.id,
                          message_text: parent.message_text,
                          attachment_type: parent.attachment_type ?? null,
                          sender_id: parent.sender_id,
                          deleted_at: parent.deleted_at ?? null,
                        } : (m.reply_to ? { id: m.reply_to, message_text: null, sender_id: "", deleted_at: new Date().toISOString() } : null)}
                        parentSenderName={parentSenderName}
                      />

                      {mine && !m.deleted_at && (
                        <div className="mt-0.5 flex items-center justify-end gap-0.5">
                          {seenInfo.seen ? (
                            <div
                              className="group/seen relative cursor-default"
                              title={
                                seenInfo.seenBy.length === 1
                                  ? `Seen by ${seenInfo.seenBy[0]} at ${formatSeenTime(seenInfo.seenAt[0])}`
                                  : `Seen by ${seenInfo.seenBy.length} people`
                              }
                            >
                              <CheckCheck
                                size={12}
                                className="text-blue-500"
                              />
                              <div className="absolute bottom-5 right-0 z-10 hidden whitespace-nowrap rounded bg-[#18181B] px-2 py-1 text-[10px] text-white shadow-lg group-hover/seen:block">
                                {seenInfo.seenBy.length === 1
                                  ? `Seen by ${seenInfo.seenBy[0]} at ${formatSeenTime(seenInfo.seenAt[0])}`
                                  : `Seen by ${seenInfo.seenBy.length} people`}
                              </div>
                            </div>
                          ) : (
                            <Check
                              size={12}
                              className="text-muted-foreground"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
                })()}
              </div>

            </ScrollArea>

            {selectMode && (
              <div className="flex items-center justify-between gap-2 border-t border-border bg-card px-4 py-2">
                <div className="text-xs font-semibold">
                  {selectedIds.size} selected
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}
                    className="rounded-md border border-border px-2 py-1 text-[11px] hover:bg-accent/50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={selectedIds.size === 0}
                    onClick={() => {
                      const chosen = messages.filter((m) => selectedIds.has(m.id) && !m.deleted_at);
                      if (chosen.length === 0) return;
                      setForwardingMulti(chosen);
                    }}
                    className="rounded-md bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground disabled:opacity-50"
                  >
                    Forward{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  </button>
                </div>
              </div>
            )}

            <ChatComposer
              roomId={activeId}
              senderId={user?.id ?? null}
              archived={activeRoom.archived}
              variant="page"
              replyingTo={replyingTo}
              onCancelReply={() => setReplyingTo(null)}
            />
          </>
        )}
      </section>
      <ForwardMessageDialog
        open={!!forwardingMsg}
        message={forwardingMsg}
        myId={user?.id ?? null}
        onClose={() => setForwardingMsg(null)}
      />
      <ForwardMessageDialog
        open={!!forwardingMulti}
        messages={forwardingMulti}
        myId={user?.id ?? null}
        onClose={() => setForwardingMulti(null)}
        onForwarded={() => { setSelectMode(false); setSelectedIds(new Set()); }}
      />

      {/* Right: info panel */}
      {activeRoom && activeRoom.type === "group" && (
        <aside className="hidden w-64 flex-col border-l border-border bg-card lg:flex">
          <div className="border-b border-border p-3">
            <div className="flex items-center gap-2 text-xs font-semibold">
              <Users className="h-3 w-3" /> Group Info
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground">
              Created by {profiles[activeRoom.created_by]?.full_name || "—"}
            </div>
          </div>
          <ScrollArea className="flex-1">
            <ul className="p-2">
              {activeMembers.map((m) => {
                const p = profiles[m.user_id];
                return (
                  <li
                    key={m.id}
                    className="flex items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-accent/50"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-xs">{p?.full_name || "—"}</div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {m.role}
                      </div>
                    </div>
                    {canManageGroups && m.user_id !== activeRoom.created_by && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleRemoveMember(m.id)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
          <div className="border-t border-border p-3 text-[10px] text-muted-foreground">
            <Info className="mr-1 inline h-3 w-3" />
            Messages auto-delete after 30 days.
          </div>
        </aside>
      )}

      {/* New Group dialog */}
      <NewGroupDialog
        open={showNewGroup}
        onOpenChange={setShowNewGroup}
        profiles={profiles}
        currentUserId={user?.id || ""}
        onCreated={(id) => {
          setActiveId(id);
          loadRooms();
        }}
      />

      <NewPersonalDialog
        open={showNewPersonal}
        onOpenChange={setShowNewPersonal}
        profiles={profiles}
        currentUserId={user?.id || ""}
        existingPersonal={rooms.filter((r) => r.type === "personal")}
        members={members}
        onReady={(id) => {
          setActiveId(id);
          loadRooms();
        }}
      />

      {activeRoom && (
        <RenameDialog
          open={showRename}
          onOpenChange={setShowRename}
          room={activeRoom}
          onDone={loadRooms}
        />
      )}
      {activeRoom && (
        <AddMembersDialog
          open={showAddMembers}
          onOpenChange={setShowAddMembers}
          room={activeRoom}
          existingMemberIds={activeMembers.map((m) => m.user_id)}
          profiles={profiles}
          onDone={loadRooms}
        />
      )}
      {activeRoom && activeRoom.type === "group" && (
        <InviteMembersDialog
          open={showInvite}
          onOpenChange={setShowInvite}
          roomId={activeRoom.id}
          roomName={activeRoom.name ?? "this group"}
          profiles={profiles}
          myId={user?.id || ""}
          existingMemberIds={new Set(activeMembers.map((m) => m.user_id))}
        />
      )}
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />

    </div>
  );
}

function roomTitle(
  room: Room,
  members: Member[],
  profiles: Record<string, ProfileLite>,
  myId: string,
): string {
  if (room.type === "group") return room.name || "Untitled group";
  const other = members
    .filter((m) => m.room_id === room.id && m.user_id !== myId)
    .map((m) => profiles[m.user_id]?.full_name || "Unknown")[0];
  return other || profiles[room.created_by]?.full_name || "Personal chat";
}

function roomBadgeLabel(
  room: Room,
  members: Member[],
  profiles: Record<string, ProfileLite>,
  myId: string,
): string {
  if (room.type === "group") return "group";
  const otherId = members
    .filter((m) => m.room_id === room.id && m.user_id !== myId)
    .map((m) => m.user_id)[0];
  const p = (otherId && profiles[otherId]) || profiles[room.created_by];
  const dept = p?.department ? DEPARTMENT_LABELS[p.department] : null;
  return dept ? dept.toLowerCase() : "personal";
}

function profileDisplayName(profile: ProfileLite) {
  const department = profile.department ? DEPARTMENT_LABELS[profile.department] : null;
  return `${profile.full_name}${department ? ` (${department.toUpperCase()})` : ""}`;
}

function profileMatches(profile: ProfileLite, query: string) {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  return profile.full_name.toLowerCase().includes(term) || profileDisplayName(profile).toLowerCase().includes(term);
}

function roomOtherUserId(room: Room, members: Member[], myId: string): string | null {
  if (room.type !== "personal") return null;
  return members.filter((m) => m.room_id === room.id).find((m) => m.user_id !== myId)?.user_id ?? null;
}

function isSameDay(a: string, b: string) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function formatDateLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (isSameDay(iso, today.toISOString())) return "Today";
  if (isSameDay(iso, yesterday.toISOString())) return "Yesterday";
  if (d.getFullYear() === today.getFullYear()) {
    return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });
  }
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

function DateSeparator({ iso }: { iso: string }) {
  return (
    <div className="my-3 flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <span className="whitespace-nowrap px-2 text-[10px] font-medium text-muted-foreground">
        {formatDateLabel(iso)}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

function computeSeenInfo(
  message: Message,
  roomMembers: Member[],
  profiles: Record<string, ProfileLite>,
  currentUserId: string,
): { seen: boolean; seenBy: string[]; seenAt: string[] } {
  if (message.sender_id !== currentUserId) {
    return { seen: false, seenBy: [], seenAt: [] };
  }
  const seenBy: string[] = [];
  const seenAt: string[] = [];
  roomMembers
    .filter((m) => m.user_id !== currentUserId)
    .forEach((m) => {
      if (
        m.last_read_at &&
        new Date(m.last_read_at) >= new Date(message.created_at)
      ) {
        const p = profiles[m.user_id];
        seenBy.push(p?.full_name ?? "Someone");
        seenAt.push(m.last_read_at);
      }
    });
  return { seen: seenBy.length > 0, seenBy, seenAt };
}

function formatSeenTime(isoString: string): string {
  return formatMessageTime(isoString);
}


// ---------- Dialogs ----------

function NewGroupDialog({
  open,
  onOpenChange,
  profiles,
  currentUserId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  profiles: Record<string, ProfileLite>;
  currentUserId: string;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const list = Object.values(profiles).filter(
    (p) =>
      p.id !== currentUserId &&
      p.status === "active" &&
      profileMatches(p, filter),
  );

  const create = async () => {
    if (isCreating) return;
    if (!name.trim()) {
      toast.error("Group name required");
      return;
    }
    if (selected.size === 0) {
      toast.error("Pick at least one member to invite");
      return;
    }
    setIsCreating(true);
    try {
      // 1. Create room
      const { data, error } = await sb
        .from("chat_rooms")
        .insert({ name: name.trim(), type: "group", created_by: currentUserId })
        .select()
        .single();
      if (error || !data) { toast.error(error?.message || "Failed"); return; }
      const room = data as Room;
      // 2. Creator joins as admin immediately
      const { error: mErr } = await sb.from("chat_members").insert({
        room_id: room.id,
        user_id: currentUserId,
        role: "admin" as const,
      });
      if (mErr) { toast.error(mErr.message); return; }
      // 3. Insert pending invites for the rest
      const invitedIds = Array.from(selected);
      const { data: invites, error: iErr } = await sb
        .from("chat_invites")
        .insert(invitedIds.map((uid) => ({
          room_id: room.id,
          invited_user: uid,
          invited_by: currentUserId,
          status: "pending" as const,
        })))
        .select();
      if (iErr) { toast.error(iErr.message); return; }
      // 4. Notifications
      const myName = profiles[currentUserId]?.full_name ?? "Someone";
      const notifRows = (invites ?? []).map((inv: any) => ({
        recipient_id: inv.invited_user as string,
        type: "chat_invite",
        title: `${myName} invited you to "${name.trim()}"`,
        body: "Tap to accept or decline.",
        related_table: "chat_invites",
        related_id: inv.id as string,
        link: "/chat",
      }));
      if (notifRows.length > 0) {
        await sb.from("notifications").insert(notifRows);
      }
      toast.success("Group created, invites sent");
      onOpenChange(false);
      setName("");
      setSelected(new Set());
      onCreated(room.id);
    } finally {
      setIsCreating(false);
    }
  };



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New group chat</DialogTitle>
          <DialogDescription>
            Create a group and add members. Only admin/moderator can manage groups.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Group name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium">Add members</label>
            <Input
              placeholder="Search…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="mb-2"
            />
            <div className="max-h-60 overflow-y-auto rounded border border-border">
              {list.map((p) => (
                <label
                  key={p.id}
                  className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent/50"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={(e) => {
                      const n = new Set(selected);
                      if (e.target.checked) n.add(p.id);
                      else n.delete(p.id);
                      setSelected(n);
                    }}
                  />
                  <span className="truncate">{profileDisplayName(p)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={create} disabled={isCreating}>
            {isCreating ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            {isCreating ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>

      </DialogContent>
    </Dialog>
  );
}

function NewPersonalDialog({
  open,
  onOpenChange,
  profiles,
  currentUserId,
  existingPersonal,
  members,
  onReady,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  profiles: Record<string, ProfileLite>;
  currentUserId: string;
  existingPersonal: Room[];
  members: Member[];
  onReady: (id: string) => void;
}) {
  const [filter, setFilter] = useState("");
  const [startingId, setStartingId] = useState<string | null>(null);
  const list = Object.values(profiles).filter(
    (p) =>
      p.id !== currentUserId &&
      p.status === "active" &&
      profileMatches(p, filter),
  );

  const start = async (otherId: string) => {
    if (startingId) return;
    setStartingId(otherId);
    try {
      const { data: roomId, error } = await sb.rpc("get_or_create_personal_room", {
        _other_user_id: otherId,
      });
      if (error || !roomId) {
        toast.error(error?.message || "Failed");
        return;
      }
      onReady(roomId as string);
      onOpenChange(false);
    } finally {
      setStartingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start personal chat</DialogTitle>
          <DialogDescription>Pick a person to chat with 1-to-1.</DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Search…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="max-h-72 overflow-y-auto rounded border border-border">
          {list.map((p) => (
            <button
              key={p.id}
              onClick={() => start(p.id)}
              disabled={startingId !== null}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-xs hover:bg-accent/50 disabled:opacity-50"
            >
              <span className="truncate">{profileDisplayName(p)}</span>
              {startingId === p.id && <Loader2 className="h-3 w-3 animate-spin" />}
            </button>
          ))}
        </div>

      </DialogContent>
    </Dialog>
  );
}

function RenameDialog({
  open,
  onOpenChange,
  room,
  onDone,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  room: Room;
  onDone: () => void;
}) {
  const [name, setName] = useState(room.name || "");
  useEffect(() => setName(room.name || ""), [room.id, room.name]);
  const save = async () => {
    if (!name.trim()) return;
    const { error } = await sb
      .from("chat_rooms")
      .update({ name: name.trim() })
      .eq("id", room.id);
    if (error) toast.error(error.message);
    else {
      onOpenChange(false);
      onDone();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename group</DialogTitle>
        </DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddMembersDialog({
  open,
  onOpenChange,
  room,
  existingMemberIds,
  profiles,
  onDone,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  room: Room;
  existingMemberIds: string[];
  profiles: Record<string, ProfileLite>;
  onDone: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  useEffect(() => setSelected(new Set()), [open]);
  const list = Object.values(profiles).filter(
    (p) =>
      !existingMemberIds.includes(p.id) &&
      p.status === "active" &&
      profileMatches(p, filter),
  );
  const save = async () => {
    if (selected.size === 0) return;
    const rows = Array.from(selected).map((uid) => ({
      room_id: room.id,
      user_id: uid,
      role: "member" as const,
    }));
    const { error } = await sb.from("chat_members").insert(rows);
    if (error) toast.error(error.message);
    else {
      onOpenChange(false);
      onDone();
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add members</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Search…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="max-h-72 overflow-y-auto rounded border border-border">
          {list.map((p) => (
            <label
              key={p.id}
              className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-accent/50"
            >
              <input
                type="checkbox"
                checked={selected.has(p.id)}
                onChange={(e) => {
                  const n = new Set(selected);
                  if (e.target.checked) n.add(p.id);
                  else n.delete(p.id);
                  setSelected(n);
                }}
              />
              <span className="truncate">{profileDisplayName(p)}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={selected.size === 0}>
            Add {selected.size > 0 && `(${selected.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteMembersDialog({
  open,
  onOpenChange,
  roomId,
  roomName,
  profiles,
  myId,
  existingMemberIds,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  roomId: string;
  roomName: string;
  profiles: Record<string, ProfileLite>;
  myId: string;
  existingMemberIds: Set<string>;
}) {
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setPicked(new Set());
    (async () => {
      const { data } = await sb
        .from("chat_invites")
        .select("invited_user")
        .eq("room_id", roomId)
        .eq("status", "pending");
      setPendingIds(new Set((data ?? []).map((r: any) => r.invited_user as string)));
    })();
  }, [open, roomId]);

  const list = Object.values(profiles)
    .filter((p) => p.id !== myId && p.status === "active" && !existingMemberIds.has(p.id) && !pendingIds.has(p.id))
    .filter((p) => profileMatches(p, q));

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  const submit = async () => {
    if (submitting) return;
    if (picked.size === 0) { toast.error("Pick at least one person to invite"); return; }
    setSubmitting(true);
    try {
      const invitedIds = Array.from(picked);
      const { data: invites, error } = await sb
        .from("chat_invites")
        .insert(invitedIds.map((uid) => ({
          room_id: roomId,
          invited_user: uid,
          invited_by: myId,
          status: "pending" as const,
        })))
        .select();
      if (error) { toast.error(error.message); return; }
      const myName = profiles[myId]?.full_name ?? "Someone";
      const notifRows = (invites ?? []).map((inv: any) => ({
        recipient_id: inv.invited_user as string,
        type: "chat_invite",
        title: `${myName} invited you to "${roomName}"`,
        body: "Tap to accept or decline.",
        related_table: "chat_invites",
        related_id: inv.id as string,
        link: "/chat",
      }));
      if (notifRows.length > 0) {
        await sb.from("notifications").insert(notifRows);
      }
      toast.success(`Invited ${invitedIds.length} ${invitedIds.length === 1 ? "person" : "people"}`);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Invite members</DialogTitle>
          <DialogDescription>Send an invite to join {roomName}.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Search people..." value={q} onChange={(e) => setQ(e.target.value)} />
        <ScrollArea className="h-56">
          <ul>
            {list.length === 0 ? (
              <li className="px-2 py-4 text-center text-xs text-muted-foreground">No one to invite.</li>
            ) : (
              list.map((p) => (
                <li key={p.id}>
                  <label className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-accent/50">
                    <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle(p.id)} />
                    <span className="flex-1 truncate">{profileDisplayName(p)}</span>
                  </label>
                </li>
              ))
            )}
          </ul>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || picked.size === 0}>
            {submitting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            {submitting ? "Sending…" : `Invite ${picked.size > 0 ? `(${picked.size})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
